/**
 * UTM & Source Tracking Utility
 * Captures, persists, and provides traffic source attribution data.
 */

const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid'
];

const LOCAL_STORAGE_KEY = 'kuku_first_touch_utm';
const SESSION_STORAGE_KEY = 'kuku_last_touch_utm';
const EXPIRY_DAYS = 30;

/**
 * Capture UTM & traffic source data from window location and document referrer
 */
export const captureUtmData = () => {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  const captured = {};
  let hasNewUtm = false;

  // Parse UTMs & Click IDs from query string
  UTM_PARAMS.forEach((param) => {
    const value = urlParams.get(param);
    if (value) {
      captured[param] = value.trim();
      hasNewUtm = true;
    }
  });

  // If new UTM parameters or click IDs exist on URL, update session storage
  if (hasNewUtm) {
    captured['referrer'] = document.referrer || captured['referrer'] || '';
    captured['landing_page'] = window.location.href;
    captured['captured_at'] = new Date().toISOString();

    // 1. Save to sessionStorage (Last-Touch)
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(captured));
    } catch (e) {
      console.warn('UTM Session Storage error:', e);
    }

    // 2. Save to localStorage if First-Touch doesn't exist yet
    try {
      const existingFirstTouch = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (!existingFirstTouch) {
        const firstTouchData = {
          ...captured,
          expires_at: new Date(Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString()
        };
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(firstTouchData));
      }
    } catch (e) {
      console.warn('UTM Local Storage error:', e);
    }
  }
};

/**
 * Retrieve merged UTM attribution payload for API requests (Last-Touch takes priority over First-Touch)
 */
export const getUtmPayload = () => {
  let lastTouch = {};
  let firstTouch = {};

  try {
    const sessionData = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (sessionData) lastTouch = JSON.parse(sessionData);

    const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (localData) {
      const parsedLocal = JSON.parse(localData);
      // Check expiry
      if (new Date(parsedLocal.expires_at) > new Date()) {
        firstTouch = parsedLocal;
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
      }
    }
  } catch (e) {
    console.warn('UTM retrieval error:', e);
  }

  // Priority: Last-Touch > First-Touch
  return {
    utm_source: lastTouch.utm_source || firstTouch.utm_source || null,
    utm_medium: lastTouch.utm_medium || firstTouch.utm_medium || null,
    utm_campaign: lastTouch.utm_campaign || firstTouch.utm_campaign || null,
    utm_term: lastTouch.utm_term || firstTouch.utm_term || null,
    utm_content: lastTouch.utm_content || firstTouch.utm_content || null,
    gclid: lastTouch.gclid || firstTouch.gclid || null,
    fbclid: lastTouch.fbclid || firstTouch.fbclid || null,
    referrer: lastTouch.referrer || firstTouch.referrer || (typeof document !== 'undefined' ? document.referrer : null),
    landing_page: lastTouch.landing_page || firstTouch.landing_page || (typeof window !== 'undefined' ? window.location.href : null),
  };
};
